import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { acquisitionApi } from '../utils/api';
import { trackEvent } from '../utils/observability';
import { registerLinkFromMiniScan } from '../utils/miniScanHandoff';
import AiContentNotice from './AiContentNotice';

/**
 * MiniScanConsumerResult — rezultatul unui mini-scan v2 (pipeline "consumator",
 * MINISCAN_SPEC v1): verdictul brutal, citatul-screenshot, granularitatea pe
 * produs, obiecțiile numărate, upsell-ul single-engine și CTA-ul.
 *
 * Randat IDENTIC in widget (LandingPage) si pe pagina publica /scan/:scan_id —
 * aceeasi regula ca la <MiniScanResult> (v1): o singura componenta, nu doua
 * copii de JSX care diverg la prima modificare.
 *
 * POARTA DE EMAIL E HIBRIDA si traieste AICI, nu in footer-ul widgetului:
 * backendul serveste teaser (gate.locked=true; headline + citat) si deblocheaza
 * restul abia dupa email (result_full pe POST /mini-scan/lead). Pagina
 * partajata primeste si ea teaser cand scanul n-a fost deblocat de nimeni —
 * deci formularul de deblocare trebuie sa existe pe AMBELE suprafete, adica in
 * componenta comuna. Parintele primeste payload-ul complet prin `onUnlocked`.
 *
 * Ce NU se afla in client cand gate.locked: produse_verdict, obiectii_count,
 * ton_breakdown. Blur-ul pe citat e doar vizual (textul e in teaser, scurgere
 * acceptata); sectiunile gated insa NU sunt randate-ascunse, ci lipsesc din
 * payload — gating-ul real e server-side, prin view-uri whitelist.
 *
 * Lista obiectiilor ramane blurata SI DUPA deblocare, prin design: textele nu
 * sunt trimise niciodata clientului (doar numarul), barele sunt placeholder-e.
 * Curiosity gap-ul e mecanismul de upsell al raportului RRI complet.
 */

const GOLD = 'var(--gold, #D4AF37)';
const MONO = 'DM Mono, monospace';
const SERIF = 'Playfair Display, serif';
const BODY = 'Cormorant Garamond, serif';

const PANEL = {
  background: 'var(--surface, #FFFFFF)',
  border: `2px solid ${GOLD}`,
  borderRadius: 16,
  padding: 'clamp(18px, 5vw, 32px)',
  maxWidth: 780,
  margin: '0 auto',
  boxShadow: '0 20px 60px rgba(212,175,55,0.15)',
};

export default function MiniScanConsumerResult({
  result,
  containerRef = null,
  onReset = null,
  onUnlocked = null,
  footer = null,
  ctaUtmSource = 'mini_scan',
  ctaEvent = 'mini_scan_cta_clicked',
}) {
  const { t } = useTranslation();
  const verdict = result.verdict || {};
  const locked = result.gate?.locked !== false;
  const notFound = verdict.key === 'not_found';
  const partial = (result.responses_ok ?? 9) < (result.total_conversatii || 9);

  return (
    <div ref={containerRef} style={PANEL}>
      {/* ── Antet ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: 2, color: 'var(--muted, #888)', textTransform: 'uppercase', marginBottom: 6 }}>
            {verdict.categorie || result.classification?.categorie || t('app.mini_scan.v2.eyebrow_generic')}
          </div>
          <div style={{ fontFamily: SERIF, fontSize: 'clamp(22px, 6vw, 30px)', fontWeight: 700, color: 'var(--ink, #0A1428)', wordBreak: 'break-word' }}>
            {result.brand_name}
          </div>
        </div>
        {onReset && (
          <button onClick={onReset} style={ghostBtn}>{t('app.mini_scan.another_brand')}</button>
        )}
      </div>

      {/* ── Banda de masuratori ─────────────────────────────────────────
          Cifrele astea soseau in teaser de la inceput (`visibility_pct`,
          `aparitii`, `dueluri_pierdute`, `verdict.lipsesti_din`) si nu le
          afisa nimeni: rezultatul era o singura propozitie, deci arata a
          jucarie, nu a masuratoare. Sunt exact ce screenshot-eaza un director
          de marketing, deci stau sus, mari, inaintea verdictului.
          La `not_found` banda dispare: patru zerouri nu sunt o masuratoare. */}
      {!notFound && <MetricStrip t={t} result={result} />}

      {/* ── 1. Verdictul brutal — headline-ul, dupa cheia stabila din backend ── */}
      <div style={{ padding: 'clamp(16px, 4vw, 24px)', background: 'rgba(10,20,40,0.04)', borderLeft: `4px solid ${GOLD}`, borderRadius: 12, marginBottom: 18 }}>
        <h2 style={{ fontFamily: SERIF, fontSize: 'clamp(20px, 5.5vw, 28px)', fontWeight: 800, color: 'var(--ink, #0A1428)', margin: 0, lineHeight: 1.25 }}>
          {verdictHeadline(t, result)}
        </h2>
        {notFound && (
          <p style={{ fontFamily: BODY, fontSize: 16, color: 'var(--muted)', margin: '10px 0 0', lineHeight: 1.55 }}>
            {t('app.mini_scan.v2.verdict.not_found_body')}
          </p>
        )}
      </div>

      {/* ══ SECTIUNEA 1 — „Cine e recomandat cand un client intreaba" ══
          GRATUITA, deliberat: e partea obiectiva si partajabila a rezultatului.
          Emailul cumpara TEXTELE din sectiunile 3 si 4, nu cifrele de aici. */}
      {!notFound && <Leaderboard t={t} result={result} />}

      {/* Citatul, ca dovada a sectiunii 1 — NEBLURAT din 2026-08-19: o pata
          blurata intr-o sectiune gratuita nu are ce comunica, iar textul era
          oricum in payload-ul teaser, citibil din devtools. */}
      {result.citat?.text && (
        <section style={{ marginBottom: 20 }}>
          <div style={{ fontFamily: MONO, fontSize: 10, color: 'var(--muted)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>
            {t('app.mini_scan.v2.quote_label')}
          </div>
          <div style={{
            background: 'rgba(0,0,0,0.05)', borderRadius: '16px 16px 16px 4px',
            padding: '14px 16px', maxWidth: '92%',
          }}>
            <div style={{ fontFamily: MONO, fontSize: 10, color: 'var(--muted)', marginBottom: 6 }}>
              ChatGPT
            </div>
            <div style={{ fontFamily: BODY, fontSize: 16.5, color: 'var(--ink)', lineHeight: 1.55 }}>
              „{result.citat.text}"
            </div>
          </div>
          {result.citat.intrebare && (
            <div style={{ fontFamily: MONO, fontSize: 10.5, color: 'var(--muted)', marginTop: 6 }}>
              {t('app.mini_scan.v2.quote_question', { intrebare: result.citat.intrebare })}
            </div>
          )}
          {/* AI Act art. 50(4): citatul e text produs de un LLM tert, reprodus literal. */}
          <AiContentNotice variant="verbatim" style={{ marginTop: 6 }} />
        </section>
      )}

      {/* ══ SECTIUNEA 2 — „Pe ce loc esti" ══ GRATUITA ══ */}
      {!notFound && <RankSection t={t} result={result} />}

      {/* ── Poarta de email ──────────────────────────────────────────────
          Se randeaza SI pe `not_found`: pana la 2026-08-19 acei prospecti nu
          vedeau nicio poarta, deci se pierdeau complet — desi tocmai aflasera
          ca AI-ul nu stie ca exista, adica exact durerea pe care o vindem. */}
      {locked && <ConsumerGate result={result} onUnlocked={onUnlocked} notFound={notFound} />}

      {/* ══ SECTIUNEA 3 — „Ce afirma AI-ul ca FAPT despre tine" ══ EMAIL ══
          Copy-ul nu spune NICIODATA „e fals": in scanul gratuit nu exista
          ground truth. Spune „verifica-le" — iar clientul, care isi stie
          propriile date, descopera singur eroarea. Verificarea contra
          registrelor oficiale e exact ce se vinde mai departe. */}
      {!notFound && result.afirmatii_count > 0 && (
        <GatedSection
          t={t}
          locked={locked}
          title={t('app.mini_scan.v2.sections.s3_title', { n: result.afirmatii_count })}
          sub={locked
            ? t('app.mini_scan.v2.sections.s3_sub_locked')
            : t('app.mini_scan.v2.sections.s3_sub_unlocked')}
          count={result.afirmatii_count}
          items={result.afirmatii_factuale}
          footer={t('app.mini_scan.v2.sections.s3_disclaimer')}
        />
      )}

      {/* ══ SECTIUNEA 4 — „Ce spune AI-ul impotriva ta" ══ EMAIL ══ */}
      {!notFound && result.obiectii_count > 0 && (
        <GatedSection
          t={t}
          locked={locked}
          title={t('app.mini_scan.v2.sections.s4_title', { n: result.obiectii_count })}
          sub={tonSummary(t, result.ton_breakdown)}
          count={result.obiectii_count}
          items={result.obiectii_publicabile}
        />
      )}

      {/* ── Transparenta esantionului ── */}
      {!notFound && (
        <div style={{ fontFamily: MONO, fontSize: 10.5, color: 'var(--muted)', lineHeight: 1.6, marginBottom: 18 }}>
          {t('app.mini_scan.v2.sample_note', { model: result.model || 'ChatGPT' })}
          {partial && (
            <div style={{ color: '#B7791F', marginTop: 4 }}>
              {t('app.mini_scan.v2.partial_note', {
                n: (result.total_conversatii || 9) - (result.responses_ok || 0),
              })}
            </div>
          )}
        </div>
      )}

      {/* Verdictul, categoria si cifrele de mai sus sunt derivate din raspunsuri
          AI. Deliberat IN AFARA gate-ului `!notFound`: pe not_found ramane pe
          ecran o clasificare produsa tot de model. */}
      <AiContentNotice variant="derived" style={{ marginTop: 0, marginBottom: 18 }} />

      {/* ── 5. Upsell single-engine ── */}
      <div style={{
        fontFamily: BODY, fontSize: 16, color: 'var(--ink)', lineHeight: 1.6,
        padding: '12px 14px', borderRadius: 10, marginBottom: 18,
        background: 'rgba(212,175,55,0.07)', borderLeft: `3px solid ${GOLD}`,
      }}>
        {t('app.mini_scan.v2.upsell')}
      </div>

      {footer}

      {/* ── 6. CTA — identic pe toate verdictele (regula HubSpot din v1) ── */}
      <div style={{ background: 'linear-gradient(135deg, #1a1610 0%, #2a2620 100%)', color: '#FFF', padding: 'clamp(16px, 4vw, 24px)', borderRadius: 12 }}>
        <div style={{ fontFamily: SERIF, fontSize: 'clamp(17px, 4.5vw, 20px)', fontWeight: 700, marginBottom: 10 }}>
          {t('app.mini_scan.cta_title')}
        </div>
        <div style={{ fontFamily: BODY, fontSize: 15, lineHeight: 1.6, marginBottom: 16, opacity: 0.9 }}>
          {t('app.mini_scan.v2.cta_body')}
        </div>
        <Link
          to={registerLinkFromMiniScan(result, ctaUtmSource)}
          onClick={() => trackEvent(ctaEvent, { verdict: verdict.key || 'unknown', scan_version: 2 })}
          style={{
            display: 'inline-block', background: GOLD, color: '#0A1428',
            padding: '13px 26px', borderRadius: 10, textDecoration: 'none',
            fontFamily: MONO, fontSize: 12, fontWeight: 700, letterSpacing: 1.4,
          }}
        >
          {t('app.mini_scan.v2.cta_button')}
        </Link>
      </div>
    </div>
  );
}

/**
 * SECTIUNEA 1 — clasamentul. Raspunde la „cand un LLM recomanda, cine e
 * recomandat in industria mea?".
 *
 * ONESTITATE, punctul cel mai sensibil al ecranului: baza NU e industria, ci
 * cele cateva intrebari in care un client cere o recomandare fara sa pomeneasca
 * vreun brand. Eticheta de sub titlu spune exact asta si NU are voie sa devina
 * „in industria ta" — doua intrebari nu sunt un recensamant.
 * Intrebarile despre PRODUSE lipsesc deliberat din baza: ele raspund corect cu
 * marci de produs (Nurofen, Vichy), care ar contamina un clasament de firme.
 */
function Leaderboard({ t, result }) {
  const cl = result.clasament;
  const randuri = (cl && cl.randuri) || [];
  const categorie = result.verdict?.categorie || result.classification?.categorie_consumator;

  // Scan salvat INAINTE de clasament (linkuri partajate vechi): cheia lipseste
  // cu totul. Sectiunea nu se randeaza deloc — mesajul de gol ar spune „nicio
  // intrebare nu a numit vreo firma", adica o afirmatie falsa despre o
  // masuratoare care pur si simplu nu s-a facut atunci.
  if (!cl) return null;

  if (randuri.length === 0) {
    return (
      <section style={{ marginBottom: 20 }}>
        <SectionTitle>{t('app.mini_scan.v2.sections.s1_title', { categorie })}</SectionTitle>
        <div style={{ fontFamily: MONO, fontSize: 11, color: 'var(--muted)', lineHeight: 1.6 }}>
          {t('app.mini_scan.v2.sections.s1_empty')}
        </div>
      </section>
    );
  }

  const max = Math.max(...randuri.map(r => r.mentiuni), 1);
  return (
    <section style={{ marginBottom: 20 }}>
      <SectionTitle>{t('app.mini_scan.v2.sections.s1_title', { categorie })}</SectionTitle>
      <div style={{ fontFamily: MONO, fontSize: 10.5, color: 'var(--muted)', lineHeight: 1.6, marginBottom: 12 }}>
        {t('app.mini_scan.v2.sections.s1_sub', { baza: cl.baza, categorie })}
      </div>
      {randuri.map((r, i) => (
        <div key={r.nume} style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px',
          borderRadius: 8, marginBottom: 4,
          background: r.este_brandul ? 'rgba(212,175,55,0.16)' : 'transparent',
          border: r.este_brandul ? '1px solid rgba(212,175,55,0.45)' : '1px solid transparent',
        }}>
          <span style={{ fontFamily: MONO, fontSize: 11, color: 'var(--muted)', minWidth: 18 }}>
            {i + 1}.
          </span>
          <span style={{
            fontFamily: BODY, fontSize: 16, color: 'var(--ink)', flex: '1 1 auto',
            fontWeight: r.este_brandul ? 700 : 400, wordBreak: 'break-word',
          }}>
            {r.nume}
            {r.este_brandul && (
              <span style={{ fontFamily: MONO, fontSize: 9.5, letterSpacing: 1.4, color: '#8B6914', marginLeft: 7 }}>
                {t('app.mini_scan.v2.sections.s1_you')}
              </span>
            )}
          </span>
          <span aria-hidden="true" style={{
            width: `${Math.round((r.mentiuni / max) * 68)}px`, height: 8, borderRadius: 4,
            background: r.este_brandul ? GOLD : 'rgba(10,20,40,0.22)', flex: '0 0 auto',
          }} />
          <span style={{ fontFamily: MONO, fontSize: 11, color: 'var(--muted)', minWidth: 54, textAlign: 'right' }}>
            {t('app.mini_scan.v2.sections.s1_mentions', { n: r.mentiuni, baza: cl.baza })}
          </span>
        </div>
      ))}
    </section>
  );
}

/** SECTIUNEA 2 — „pe ce loc esti", plus granularitatea pe produs. */
function RankSection({ t, result }) {
  const cl = result.clasament;
  const produse = result.produse_verdict || [];
  const areLoc = !!(cl && cl.loc && cl.din);
  if (!areLoc && produse.length === 0) return null;
  const randulMeu = areLoc ? cl.randuri.find(r => r.este_brandul) : null;

  return (
    <section style={{ marginBottom: 20 }}>
      <SectionTitle>{t('app.mini_scan.v2.sections.s2_title')}</SectionTitle>
      {areLoc && (
        <div style={{
          fontFamily: SERIF, fontSize: 'clamp(19px, 5vw, 25px)', fontWeight: 800,
          color: 'var(--ink)', lineHeight: 1.3, marginBottom: 6,
        }}>
          {randulMeu && randulMeu.mentiuni === 0
            ? t('app.mini_scan.v2.sections.s2_rank_none')
            : t('app.mini_scan.v2.sections.s2_rank', { loc: cl.loc, din: cl.din })}
        </div>
      )}
      <div style={{ fontFamily: MONO, fontSize: 10.5, color: 'var(--muted)', lineHeight: 1.7, marginBottom: produse.length ? 10 : 0 }}>
        {t('app.mini_scan.v2.sections.s2_detail', {
          aparitii: result.aparitii,
          total: result.total_conversatii || 9,
          dueluri: result.dueluri_pierdute,
        })}
      </div>
      {produse.map((p, i) => (
        <div key={i} style={{
          fontFamily: BODY, fontSize: 16, color: 'var(--ink)', lineHeight: 1.6,
          padding: '8px 12px', borderRadius: 8, marginBottom: 5, background: 'rgba(0,0,0,0.03)',
        }}>
          {t(`app.mini_scan.v2.product_${p.key}`, { produs: p.produs, concurent: p.concurent })}
        </div>
      ))}
    </section>
  );
}

/**
 * SECTIUNILE 3 si 4 — aceeasi mecanica: numarul e gratuit, TEXTELE cer email.
 *
 * Diferenta fata de v2 initial: acum exista text REAL in spatele barelor, care
 * chiar se dezvaluie la deblocare. Pana la 2026-08-19 barele nu acopereau
 * nimic — cine deschidea devtools nu gasea niciun text, ceea ce e mai rau
 * decat sa nu promiti nimic.
 */
function GatedSection({ t, locked, title, sub, count, items, footer }) {
  return (
    <section style={{ marginBottom: 20 }}>
      <div style={{
        fontFamily: SERIF, fontSize: 'clamp(16px, 4.5vw, 19px)', fontWeight: 700,
        color: 'var(--ink)', marginBottom: 6, lineHeight: 1.4,
      }}>
        {title}
      </div>
      {sub && (
        <div style={{ fontFamily: MONO, fontSize: 10.5, color: 'var(--muted)', lineHeight: 1.6, marginBottom: 10 }}>
          {sub}
        </div>
      )}

      {locked ? (
        <div aria-hidden="true">
          {Array.from({ length: Math.min(count, 5) }).map((_, i) => (
            <div key={i} style={{
              height: 16, borderRadius: 6, marginBottom: 8,
              width: `${88 - (i * 13) % 34}%`,
              background: 'linear-gradient(90deg, rgba(10,20,40,0.16), rgba(10,20,40,0.07))',
              filter: 'blur(4px)',
            }} />
          ))}
        </div>
      ) : (
        (items || []).map((item, i) => (
          <div key={i} style={{
            padding: '10px 13px', borderRadius: 8, marginBottom: 6,
            background: 'rgba(0,0,0,0.035)', borderLeft: '3px solid rgba(10,20,40,0.18)',
          }}>
            <div style={{ fontFamily: BODY, fontSize: 16, color: 'var(--ink)', lineHeight: 1.55 }}>
              „{item.text}"
            </div>
            {item.intrebare && (
              <div style={{ fontFamily: MONO, fontSize: 9.5, color: 'var(--muted)', marginTop: 5, lineHeight: 1.5 }}>
                {t('app.mini_scan.v2.sections.from_question', { intrebare: item.intrebare })}
              </div>
            )}
          </div>
        ))
      )}

      {footer && (
        <div style={{ fontFamily: MONO, fontSize: 10, color: 'var(--muted)', lineHeight: 1.6, marginTop: 8 }}>
          {footer}
        </div>
      )}
    </section>
  );
}

/** Titlu de sectiune — o singura definitie, ca cele patru sa arate identic. */
function SectionTitle({ children }) {
  return (
    <div style={{
      fontFamily: MONO, fontSize: 10, color: 'var(--muted)', letterSpacing: 2,
      textTransform: 'uppercase', marginBottom: 8,
    }}>
      {children}
    </div>
  );
}

/** Tonul, in sfarsit randat: `ton_breakdown` se trimitea la client din
 *  2026-08-14 si nu-l afisa nimeni. Etichetat ca judecata a modelului. */
function tonSummary(t, ton) {
  if (!ton) return null;
  const neg = (ton.negativ || 0) + (ton.mixt || 0);
  if (!neg) return null;
  return t('app.mini_scan.v2.sections.s4_tone', { n: neg });
}


/**
 * Banda de sinteza — tiparul „AEO Snapshot" (Profound) + clasificarea de
 * pozitie (HubSpot AI Search Grader): cateva metrici sus, fiecare cu o
 * calificare, plus o eticheta de pozitie. Alea sunt cele doua lucruri care
 * despart un raport de un rezultat de formular.
 *
 * CE NU AFISAM, desi ar ingrosa banda: `aparitii` ca metrica separata de
 * `visibility_pct`. Sunt aceeasi masuratoare (78% ESTE 7/9), iar doua casute
 * pentru un singur semnal e fix densitatea falsa pe care un director de
 * marketing o vede din prima. Apare o singura data, cu fractia ca subtitlu.
 * Din acelasi motiv lipseste `lipsesti_din`: e complementul lui `aparitii`.
 *
 * `acoperire` (responses_ok/total) NU e un scor de brand, ci calitatea
 * esantionului — sta in banda fiindca transparenta metodei e ce face
 * masuratoarea credibila, si e singurul loc unde un scan partial se vede
 * inainte de concluzii.
 */
function MetricStrip({ t, result }) {
  const v = result.verdict || {};
  const total = result.total_conversatii || 9;
  const vis = result.visibility_pct;
  const duels = v.dueluri_pierdute;
  const ok = result.responses_ok;

  // Praguri de AFISARE, alese explicit (nu masurate): sub 40% brandul lipseste
  // din majoritatea conversatiilor, peste 70% e prezent in majoritatea lor.
  // Le tinem aici, intr-un singur loc, ca sa nu se rasfire prin JSX.
  const visRating = vis == null ? null : vis >= 70 ? 'good' : vis >= 40 ? 'fair' : 'poor';
  const duelRating = duels == null ? null : duels === 0 ? 'good' : duels <= 2 ? 'fair' : 'poor';

  // Pozitia: cand exista un dominant cu nume, NUMELE e valoarea — el e partea
  // care lovaste ("Bricostore"), nu eticheta de stare. Fara nume, cade pe
  // eticheta si subtitlul generic.
  const positionKey = { dominated: 'dominated', winner: 'winner', invisible: 'invisible' }[v.key] || 'unknown';
  const named = v.key === 'dominated' && v.concurent_dominant;
  const position = named
    ? { value: v.concurent_dominant, sub: t('app.mini_scan.v2.position_dominated_by') }
    : { value: t(`app.mini_scan.v2.position_${positionKey}`), sub: t('app.mini_scan.v2.metric_position_sub') };

  const items = [
    vis != null && {
      label: t('app.mini_scan.v2.metric_visibility'),
      value: `${vis}%`,
      sub: t('app.mini_scan.v2.metric_visibility_sub', { n: result.aparitii ?? 0, total }),
      rating: visRating,
    },
    duels != null && {
      label: t('app.mini_scan.v2.metric_duels'),
      value: String(duels),
      sub: t('app.mini_scan.v2.metric_duels_sub', { total }),
      rating: duelRating,
    },
    {
      label: t('app.mini_scan.v2.metric_position'),
      value: position.value,
      sub: position.sub,
      rating: null,
      small: true,
    },
    ok != null && {
      label: t('app.mini_scan.v2.metric_coverage'),
      value: `${ok}/${total}`,
      sub: t('app.mini_scan.v2.metric_coverage_sub'),
      rating: null,
      small: true,
    },
  ].filter(Boolean);

  return (
    <section
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fit, minmax(128px, 1fr))`,
        gap: 1,
        background: 'rgba(10,20,40,0.08)',
        border: '1px solid rgba(10,20,40,0.08)',
        borderRadius: 12,
        overflow: 'hidden',
        marginBottom: 18,
      }}
    >
      {items.map((it, i) => (
        <div key={i} style={{ background: 'var(--surface, #FFFFFF)', padding: '14px 14px 12px' }}>
          <div style={{ fontFamily: MONO, fontSize: 9.5, letterSpacing: 1.6, textTransform: 'uppercase', color: 'var(--muted, #888)', marginBottom: 7 }}>
            {it.label}
          </div>
          <div style={{
            fontFamily: SERIF,
            fontSize: it.small ? 'clamp(15px, 4vw, 18px)' : 'clamp(24px, 7vw, 32px)',
            fontWeight: 700,
            color: 'var(--ink, #0A1428)',
            lineHeight: 1.1,
            wordBreak: 'break-word',
          }}>
            {it.value}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
            {it.rating && <RatingChip t={t} rating={it.rating} />}
            <span style={{ fontFamily: MONO, fontSize: 9.5, color: 'var(--muted, #888)', lineHeight: 1.4 }}>
              {it.sub}
            </span>
          </div>
        </div>
      ))}
    </section>
  );
}

/** Calificarea bun/mediu/slab de langa metrica. Verdele e rezervat lucrurilor
 *  chiar bune — un chip verde pe un scor mediocru e primul lucru care strica
 *  increderea intr-un raport. */
function RatingChip({ t, rating }) {
  const palette = {
    good: { bg: 'rgba(45,106,79,0.12)', fg: '#2D6A4F' },
    fair: { bg: 'rgba(183,121,31,0.14)', fg: '#B7791F' },
    poor: { bg: 'rgba(192,57,43,0.10)', fg: '#C0392B' },
  }[rating];
  if (!palette) return null;
  return (
    <span style={{
      fontFamily: MONO, fontSize: 9, fontWeight: 700, letterSpacing: 1,
      textTransform: 'uppercase', padding: '2px 7px', borderRadius: 999,
      background: palette.bg, color: palette.fg, whiteSpace: 'nowrap',
    }}>
      {t(`app.mini_scan.v2.rating_${rating}`)}
    </span>
  );
}

/**
 * Headline-ul verdictului, ales dupa cheia stabila din backend. Varianta fara
 * nume de concurent (`dominated` cu dominant scos la granita de publicare)
 * are copy propriu — un template cu `{{concurent}}` gol ar tipari o gaura.
 */
function verdictHeadline(t, result) {
  const v = result.verdict || {};
  const total = result.total_conversatii || 9;
  switch (v.key) {
    case 'dominated':
      return v.concurent_dominant
        ? t('app.mini_scan.v2.verdict.dominated', {
            concurent: v.concurent_dominant, n: v.dueluri_pierdute, total,
            categorie: v.categorie || t('app.mini_scan.v2.categorie_fallback'),
          })
        : t('app.mini_scan.v2.verdict.dominated_unnamed', {
            n: v.dueluri_pierdute, total,
            categorie: v.categorie || t('app.mini_scan.v2.categorie_fallback'),
          });
    case 'winner':
      return t('app.mini_scan.v2.verdict.winner', {
        n: v.aparitii, total, obiectii: v.obiectii_count,
      });
    case 'invisible':
      return t('app.mini_scan.v2.verdict.invisible', { n: v.lipsesti_din, total });
    case 'not_found':
      return t('app.mini_scan.v2.verdict.not_found_title');
    default:
      return t('app.mini_scan.v2.verdict.no_data');
  }
}

/**
 * Formularul de deblocare. Trimite lead-ul la POST /mini-scan/lead si urca
 * `result_full` la parinte prin `onUnlocked` — payload-ul complet vine DOAR
 * de la server, componenta nu "descuie" nimic local.
 *
 * Consimtamantul de newsletter e NEPREBIFAT si separat — acelasi text si
 * aceeasi versiune (`gate_newsletter_v1`, cheia i18n `app.mini_scan.gate_newsletter`)
 * ca in poarta v1: temeiurile (cerere explicita vs art. 6(1)(a)) nu se schimba
 * cu pipeline-ul. Starile 2xx raman cele trei din v1 (sent / sent_newsletter /
 * newsletter_only) — dar aici toate trei DEBLOCHEAZA, fiindca toate inseamna
 * "leadul a fost stocat".
 */
function ConsumerGate({ result, onUnlocked, notFound = false }) {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [newsletter, setNewsletter] = useState(false);
  // idle | sending | limit | error — starile de succes nu raman pe ecran:
  // sosirea lui result_full face gate.locked=false si formularul dispare.
  const [state, setState] = useState('idle');

  const submit = useCallback(async (e) => {
    e?.preventDefault();
    if (!email.trim()) return;
    setState('sending');
    try {
      const res = await acquisitionApi.miniScanLead({
        email: email.trim(),
        scan_id: result.scan_id,
        brand_name: result.brand_name,
        newsletter,
        lang: i18n.language,
      });
      const d = res?.data || {};
      trackEvent('mini_scan_email_submitted', {
        outcome: d.emailed ? 'sent' : (d.newsletter ? 'newsletter_only' : 'error'),
        newsletter: Boolean(d.newsletter),
        scan_version: 2,
      });
      if (d.result_full && onUnlocked) {
        trackEvent('mini_scan_gate_unlocked', { verdict: result.verdict?.key || 'unknown' });
        onUnlocked(d.result_full);
      } else if (!d.result_full) {
        // 2xx fara payload complet (ex. lead "lost" pe un DB cazut): formularul
        // ramane, omul poate reincerca — nu pretindem ca s-a deblocat ceva.
        setState('error');
      }
    } catch (err) {
      const limited = err.response?.status === 429;
      setState(limited ? 'limit' : 'error');
      trackEvent('mini_scan_email_failed', {
        reason: limited ? 'rate_limit' : 'error',
        status: err.response?.status || 0,
        scan_version: 2,
      });
    }
  }, [email, newsletter, result, onUnlocked, i18n.language]);

  return (
    <section style={{ background: 'rgba(0,0,0,0.03)', borderRadius: 12, padding: 'clamp(14px, 4vw, 20px)', marginBottom: 18 }}>
      <div style={{ fontFamily: SERIF, fontSize: 'clamp(17px, 4.5vw, 20px)', fontWeight: 700, color: 'var(--ink)' }}>
        {t(notFound ? 'app.mini_scan.v2.not_found_gate_title' : 'app.mini_scan.v2.gate_title')}
      </div>
      <div style={{ fontFamily: BODY, fontSize: 15, color: 'var(--muted)', margin: '6px 0 12px', lineHeight: 1.55 }}>
        {t(notFound ? 'app.mini_scan.v2.not_found_gate_body' : 'app.mini_scan.v2.gate_body')}
      </div>
      <form onSubmit={submit} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input
          type="email" required value={email} onChange={e => setEmail(e.target.value)}
          placeholder={t('app.mini_scan.gate_placeholder')}
          aria-label={t('app.mini_scan.gate_placeholder')}
          style={{ ...inputStyle, flex: '1 1 200px', minWidth: 0 }}
        />
        <button type="submit" disabled={state === 'sending'} style={{ ...primaryBtn, flex: '0 0 auto' }}>
          {state === 'sending' ? t('app.mini_scan.gate_sending') : t('app.mini_scan.v2.gate_button')}
        </button>
        <label
          htmlFor="pb-consumer-gate-newsletter"
          style={{
            display: 'flex', gap: 8, alignItems: 'flex-start', width: '100%',
            fontFamily: BODY, fontSize: 14.5, color: 'var(--muted)',
            lineHeight: 1.5, cursor: 'pointer',
          }}
        >
          <input
            id="pb-consumer-gate-newsletter"
            type="checkbox"
            checked={newsletter}
            onChange={e => setNewsletter(e.target.checked)}
            style={{ width: 16, height: 16, marginTop: 3, flex: '0 0 auto', accentColor: GOLD }}
          />
          <span>{t('app.mini_scan.gate_newsletter')}</span>
        </label>
        {state === 'limit' && (
          <div role="status" style={{ fontFamily: MONO, fontSize: 11, color: '#B7791F', width: '100%', lineHeight: 1.6 }}>
            {t('app.mini_scan.gate_error_limit')}
          </div>
        )}
        {state === 'error' && (
          <div role="alert" style={{ fontFamily: MONO, fontSize: 11, color: '#c0392b', width: '100%', lineHeight: 1.6 }}>
            {t('app.mini_scan.gate_error')}
          </div>
        )}
      </form>
    </section>
  );
}

const inputStyle = {
  padding: '14px 16px',
  fontSize: 16,
  fontFamily: MONO,
  border: '2px solid rgba(212,175,55,0.3)',
  borderRadius: 10,
  outline: 'none',
  background: 'var(--cream, #FAFAF7)',
  color: 'var(--ink, #0A1428)',
};

const primaryBtn = {
  padding: '14px 22px',
  background: `linear-gradient(135deg, ${GOLD} 0%, #B8972E 100%)`,
  color: '#0A1428',
  border: 'none',
  borderRadius: 10,
  fontFamily: MONO,
  fontSize: 12.5,
  fontWeight: 700,
  letterSpacing: 1.4,
  cursor: 'pointer',
  textTransform: 'uppercase',
};

const ghostBtn = {
  background: 'transparent',
  border: '1px solid rgba(0,0,0,0.12)',
  padding: '8px 13px',
  borderRadius: 8,
  fontFamily: MONO,
  fontSize: 11,
  cursor: 'pointer',
  color: 'var(--muted)',
  flex: '0 0 auto',
};
